import { DownloadOutlined } from '@ant-design/icons';
import { Button, message, Modal, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import YAML from 'yamljs';
import { useGetIngestionExecutionRequestQuery } from '../../../../graphql/ingestion.generated';
import { useTranslation } from '../../../../i18n/useTranslation';
import { ANTD_GRAY } from '../../../entity/shared/constants';
import { downloadFile } from '../../../search/utils/csvUtils';
import { Message } from '../../../shared/Message';
import IngestedAssets from '../IngestedAssets';
import {
    getExecutionRequestStatusDisplayColor,
    getExecutionRequestStatusDisplayText,
    getExecutionRequestStatusIcon,
    getExecutionRequestSummaryText,
    RUNNING,
    SUCCESS,
} from '../utils';

const Section = styled.div`
    display: flex;
    flex-direction: column;
    padding-bottom: 12px;
`;

const SectionHeader = styled(Typography.Title)`
    &&&& {
        padding: 0px;
        margin: 0px;
        margin-bottom: 12px;
    }
`;

const SectionSubHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SubHeaderParagraph = styled(Typography.Paragraph)`
    margin-bottom: 0px;
`;

const StatusSection = styled.div`
    border-bottom: 1px solid ${ANTD_GRAY[4]};
`;

const ResultText = styled.div`
    margin-bottom: 4px;
`;

const IngestedAssetsSection = styled.div`
    padding: 16px 0;
    border-bottom: 1px solid ${ANTD_GRAY[4]};
`;

const RecipeSection = styled.div`
    padding-top: 16px;
    border-top: 1px solid ${ANTD_GRAY[4]};
`;

const LogsSection = styled.div`
    padding-top: 16px;
`;

const ShowMoreButton = styled(Button)`
    margin: 4px;
    padding: 0px;
`;

type Props = {
    urn: string;
    name?: string;
    timestampMs?: number | null;
    visible: boolean;
    onClose: () => void;
};

const BUSINESS_GLOSARY_SOURCE_TYPE = 'datahub-business-glossary';

export const ExecutionDetailsModal = ({ urn, name, timestampMs, visible, onClose }: Props) => {
    const { t } = useTranslation();
    const [showExpandedLogs, setShowExpandedLogs] = useState(false);
    const [showExpandedRecipe, setShowExpandedRecipe] = useState(false);

    const { data, loading, error, refetch } = useGetIngestionExecutionRequestQuery({ variables: { urn } });

    const resolvedName = name || urn;

    const output: any = data?.executionRequest?.result?.report || t('No output found.');

    const timestamp = new Date(data?.executionRequest?.result?.startTimeMs ?? timestampMs ?? 0).toISOString();

    const downloadLogs = () => {
        downloadFile(output, `exec-${resolvedName}-${timestamp}.log`);
    };

    const logs = (showExpandedLogs && output) || output.slice(0, 100);
    const result = data?.executionRequest?.result?.status;

    useEffect(() => {
        const interval = setInterval(() => {
            if (result === RUNNING) refetch();
        }, 2000);

        return () => clearInterval(interval);
    });

    const ResultIcon = result && getExecutionRequestStatusIcon(result);
    const resultColor = result && getExecutionRequestStatusDisplayColor(result);
    const resultText = result && (
        <Typography.Text style={{ color: resultColor, fontSize: 14 }}>
            {ResultIcon && <ResultIcon style={{ marginRight: 4 }} />}
            {t(getExecutionRequestStatusDisplayText(result))}
        </Typography.Text>
    );
    const resultSummaryText =
        (result && <Typography.Text type="secondary">{t(getExecutionRequestSummaryText(result))}</Typography.Text>) ||
        undefined;

    const recipeJson = data?.executionRequest?.input.arguments?.find((arg) => arg.key === 'recipe')?.value;
    let recipeYaml: string;
    let isBusinessGlossarySource = false;

    try {
        const parsedRecipe = recipeJson && JSON.parse(recipeJson);
        recipeYaml = parsedRecipe && YAML.stringify(parsedRecipe, 8, 2).trim();
        isBusinessGlossarySource = parsedRecipe?.source?.type === BUSINESS_GLOSARY_SOURCE_TYPE;
    } catch (e) {
        recipeYaml = '';
    }
    const recipe = showExpandedRecipe ? recipeYaml : recipeYaml?.split('\n').slice(0, 1).join('\n');

    const areLogsExpandable = output.length > 100;
    const isRecipeExpandable = recipeYaml?.includes('\n');

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            className="f-modal"
            title={<h1>{t('Ingestion Run Details')}</h1>}
            wrapClassName="f-modal-vertical-scroll"
            footer={<Button onClick={onClose}>{t('Close')}</Button>}
        >
            {!data && loading && <Message type="loading" content={t('Loading execution details...')} />}
            {error && message.error(t('Failed to load execution details :('))}
            <Section>
                <StatusSection>
                    <Typography.Title level={5}>{t('Status')}</Typography.Title>
                    <ResultText>{resultText}</ResultText>
                    <SubHeaderParagraph>{resultSummaryText}</SubHeaderParagraph>
                </StatusSection>
                {result === SUCCESS && (
                    <IngestedAssetsSection>
                        {data?.executionRequest?.id && <IngestedAssets id={data?.executionRequest?.id} />}
                    </IngestedAssetsSection>
                )}
                <LogsSection>
                    <SectionHeader level={5}>{t('Logs')}</SectionHeader>
                    <SectionSubHeader>
                        <SubHeaderParagraph type="secondary">
                            {t('View logs that were collected during the ingestion run.')}
                        </SubHeaderParagraph>
                        <Button type="text" onClick={downloadLogs} aria-label="Download">
                            <DownloadOutlined />
                            {t('Download')}
                        </Button>
                    </SectionSubHeader>
                    <Typography.Paragraph ellipsis>
                        <pre>{`${logs}${!showExpandedLogs && areLogsExpandable ? '...' : ''}`}</pre>
                        {areLogsExpandable && (
                            <ShowMoreButton type="link" onClick={() => setShowExpandedLogs(!showExpandedLogs)}>
                                {showExpandedLogs ? t('Hide') : t('Show More')}
                            </ShowMoreButton>
                        )}
                    </Typography.Paragraph>
                </LogsSection>
                {recipe && !isBusinessGlossarySource && (
                    <RecipeSection>
                        <SectionHeader level={5}>{t('Recipe')}</SectionHeader>
                        <SectionSubHeader>
                            <SubHeaderParagraph type="secondary">
                                {t('The recipe used for this ingestion run.')}
                            </SubHeaderParagraph>
                        </SectionSubHeader>
                        <Typography.Paragraph ellipsis>
                            <pre>{`${recipe}${!showExpandedRecipe && isRecipeExpandable ? '\n...' : ''}`}</pre>
                        </Typography.Paragraph>
                        {isRecipeExpandable && (
                            <ShowMoreButton type="link" onClick={() => setShowExpandedRecipe((v) => !v)}>
                                {showExpandedRecipe ? t('Hide') : t('Show More')}
                            </ShowMoreButton>
                        )}
                    </RecipeSection>
                )}
            </Section>
        </Modal>
    );
};
